import React, { useEffect, useRef, useState } from "react";
import { Tooltip, Box } from "@mui/material";

type EllipsisTooltipProps = {
  title: React.ReactNode;
  children: React.ReactNode;
  maxWidth?: number | string;
  lines?: number;
};

export const EllipsisTooltip: React.FC<EllipsisTooltipProps> = ({
  title,
  children,
  maxWidth = "100%",
  lines = 1,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const textRef = useRef<HTMLDivElement>(null);
  const [isOverflowed, setIsOverflowed] = useState(false);

  useEffect(() => {
    const container = containerRef.current;
    const textEl = textRef.current;
    if (!container || !textEl) return;

    const check = () => {
      setIsOverflowed(
        textEl.scrollWidth > textEl.clientWidth ||
          textEl.scrollHeight > textEl.clientHeight,
      );
    };

    check();

    const ro = new ResizeObserver(check);
    ro.observe(container);

    return () => ro.disconnect();
  }, [children, lines, maxWidth]);

  return (
    <Tooltip
      title={isOverflowed ? title : ""}
      disableHoverListener={!isOverflowed}
      disableFocusListener={!isOverflowed}
      disableTouchListener={!isOverflowed}
      slotProps={{
        tooltip: {
          sx: {
            color: "#fff !important",
            fontSize: 13,
            lineHeight: 1.4,
            "& *": {
              color: "#fff !important",
            },
            "& a": {
              color: "#fff !important",
              textDecoration: "none !important",
            },
          },
        },
        arrow: {
          sx: {
            color: "#111827",
          },
        },
      }}
    >
      <Box
        ref={containerRef}
        sx={{
          maxWidth,
          width: "100%",
          minWidth: 0,
          overflow: "hidden",
          display: "block",
        }}
      >
        <Box
          ref={textRef}
          sx={{
            maxWidth: "100%",
            overflow: "hidden",
            textOverflow: "ellipsis",
            verticalAlign: "bottom",
            ...(lines > 1
              ? {
                  display: "-webkit-box",
                  WebkitLineClamp: lines,
                  WebkitBoxOrient: "vertical",
                  whiteSpace: "normal",
                }
              : {
                  display: "block",
                  whiteSpace: "nowrap",
                }),
          }}
        >
          <Box
            sx={{
              display: lines > 1 ? "block" : "inline",
              "& > p": { display: "inline", margin: 0 }, // Typography default => <p>
            }}
          >
            {children}
          </Box>
        </Box>
      </Box>
    </Tooltip>
  );
};
